/**
 * EditCampaignModal
 *
 * Modal for editing paid campaigns after deployment.
 * Supports:
 * - Quick actions (pause/resume)
 * - Budget adjustment (daily/lifetime)
 * - Flight date editing (start/end dates)
 * - Creative swapping (syncs to platform)
 * - Copy/CTA editing
 * - Version history with platform sync status
 * - Navigation to Funnel Builder for advanced editing
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    X,
    Calendar,
    DollarSign,
    Edit2,
    Image,
    Save,
    Pause,
    Play,
    ExternalLink,
    History,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    Check,
    CheckCircle,
    XCircle,
    RefreshCw,
    RotateCcw,
    Link,
    Type,
    MessageSquare
} from 'lucide-react';
import {
    pauseCampaign,
    resumeCampaign,
    updateCampaignBudget
} from '../../services/campaignActionsService';
import { CTA_OPTIONS } from '../funnel-builder/forms/AdForm';

// Campaign status options
const CAMPAIGN_STATUSES = {
    ACTIVE: { label: 'Active', color: 'var(--success-500)', icon: Play },
    PAUSED: { label: 'Paused', color: 'var(--warning-500)', icon: Pause },
    PENDING: { label: 'Pending', color: 'var(--info-500)', icon: RefreshCw },
    ENDED: { label: 'Ended', color: 'var(--color-text-tertiary)', icon: CheckCircle },
    DRAFT: { label: 'Draft', color: 'var(--color-text-secondary)', icon: Edit2 }
};

// Budget type options
const BUDGET_TYPES = [
    { value: 'daily', label: 'Daily Budget' },
    { value: 'lifetime', label: 'Lifetime Budget' }
];

const EditCampaignModal = ({
    isOpen,
    onClose,
    campaign,
    userId,
    brandId,
    onUpdate,
    onNavigateToFunnel,
    libraryItems = []  // For creative swapping
}) => {
    // Form state
    const [budget, setBudget] = useState(0);
    const [budgetType, setBudgetType] = useState('daily');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [headline, setHeadline] = useState('');
    const [primaryText, setPrimaryText] = useState('');
    const [cta, setCta] = useState('LEARN_MORE');
    const [destinationUrl, setDestinationUrl] = useState('');
    const [selectedCreativeUrl, setSelectedCreativeUrl] = useState('');

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [isPausing, setIsPausing] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [showCreativeSwapper, setShowCreativeSwapper] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [activeTab, setActiveTab] = useState('budget'); // 'budget', 'creative', 'copy'

    // Initialize form from campaign
    useEffect(() => {
        if (campaign) {
            setBudget(campaign.budget || 0);
            setBudgetType(campaign.budgetType || 'daily');
            setHeadline(campaign.headline || '');
            setPrimaryText(campaign.primaryText || campaign.description || '');
            setCta(campaign.cta || 'LEARN_MORE');
            setDestinationUrl(campaign.destinationUrl || '');
            setSelectedCreativeUrl(campaign.creative?.imageUrl || campaign.imageUrl || '');

            // Parse dates
            if (campaign.startDate) {
                const start = new Date(campaign.startDate);
                setStartDate(start.toISOString().split('T')[0]);
            }
            if (campaign.endDate) {
                const end = new Date(campaign.endDate);
                setEndDate(end.toISOString().split('T')[0]);
            }

            setHasChanges(false);
            setError(null);
            setSuccessMessage(null);
        }
    }, [campaign]);

    // Track changes
    useEffect(() => {
        if (!campaign) return;

        const changed =
            budget !== (campaign.budget || 0) ||
            budgetType !== (campaign.budgetType || 'daily') ||
            headline !== (campaign.headline || '') ||
            primaryText !== (campaign.primaryText || campaign.description || '') ||
            cta !== (campaign.cta || 'LEARN_MORE') ||
            destinationUrl !== (campaign.destinationUrl || '') ||
            selectedCreativeUrl !== (campaign.creative?.imageUrl || campaign.imageUrl || '') ||
            startDate !== (campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '') ||
            endDate !== (campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '');

        setHasChanges(changed);
    }, [budget, budgetType, headline, primaryText, cta, destinationUrl, selectedCreativeUrl, startDate, endDate, campaign]);

    // Get status config
    const statusConfig = useMemo(() => {
        const status = campaign?.status || 'PENDING';
        return CAMPAIGN_STATUSES[status] || CAMPAIGN_STATUSES.PENDING;
    }, [campaign?.status]);

    // Handle pause campaign
    const handlePause = async () => {
        if (!campaign?.platformCampaignId || !campaign?.platform) {
            setError('Cannot pause: missing campaign ID or platform');
            return;
        }

        setIsPausing(true);
        setError(null);

        try {
            await pauseCampaign(
                campaign.platformCampaignId,
                campaign.platform,
                userId,
                brandId
            );

            setSuccessMessage('Campaign paused successfully');
            onUpdate?.(campaign.id, { ...campaign, status: 'PAUSED' });

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to pause campaign:', err);
            setError(err.message || 'Failed to pause campaign');
        } finally {
            setIsPausing(false);
        }
    };

    // Handle resume campaign
    const handleResume = async () => {
        if (!campaign?.platformCampaignId || !campaign?.platform) {
            setError('Cannot resume: missing campaign ID or platform');
            return;
        }

        setIsResuming(true);
        setError(null);

        try {
            await resumeCampaign(
                campaign.platformCampaignId,
                campaign.platform,
                userId,
                brandId
            );

            setSuccessMessage('Campaign resumed successfully');
            onUpdate?.(campaign.id, { ...campaign, status: 'ACTIVE' });

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to resume campaign:', err);
            setError(err.message || 'Failed to resume campaign');
        } finally {
            setIsResuming(false);
        }
    };

    // Handle save all changes
    const handleSave = async () => {
        if (!campaign?.id || !userId) {
            setError('Cannot save: missing campaign ID or user ID');
            return;
        }

        if (!hasChanges) {
            onClose();
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            // Build updates object
            const updates = {
                budget,
                budgetType,
                headline,
                primaryText,
                cta,
                destinationUrl,
                startDate: startDate ? new Date(startDate).toISOString() : null,
                endDate: endDate ? new Date(endDate).toISOString() : null
            };

            // If creative changed, include it
            if (selectedCreativeUrl !== (campaign.creative?.imageUrl || campaign.imageUrl || '')) {
                updates.creative = {
                    ...campaign.creative,
                    imageUrl: selectedCreativeUrl
                };
                updates.imageUrl = selectedCreativeUrl;
            }

            // Create version entry for tracking
            const versionEntry = {
                version: (campaign.currentVersion || 0) + 1,
                timestamp: new Date().toISOString(),
                changedBy: userId,
                changes: buildChangeDiff(campaign, updates),
                snapshot: { ...campaign, ...updates },
                platformSynced: false, // Will be updated after sync
                syncResult: null
            };

            // If budget changed, sync to platform
            let syncResult = null;
            if (budget !== campaign.budget && campaign.platformCampaignId) {
                try {
                    syncResult = await updateCampaignBudget(
                        campaign.platformCampaignId,
                        campaign.platform,
                        budget,
                        budgetType,
                        userId,
                        brandId
                    );
                    versionEntry.platformSynced = true;
                    versionEntry.syncResult = syncResult;
                } catch (syncErr) {
                    console.error('Budget sync failed:', syncErr);
                    versionEntry.syncError = syncErr.message;
                    // Continue with local save even if sync fails
                }
            }

            // Update local state and notify parent
            const updatedCampaign = {
                ...campaign,
                ...updates,
                currentVersion: versionEntry.version,
                versions: [...(campaign.versions || []), versionEntry],
                lastModified: new Date().toISOString()
            };

            onUpdate?.(campaign.id, updatedCampaign);

            if (versionEntry.syncError) {
                setSuccessMessage('Saved locally. Platform sync failed - check notification center.');
            } else {
                setSuccessMessage('Changes saved and synced to platform');
            }

            setTimeout(() => {
                setSuccessMessage(null);
                onClose();
            }, 2000);

        } catch (err) {
            console.error('Failed to save campaign:', err);
            setError(err.message || 'Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    // Build change diff for version history
    const buildChangeDiff = (original, updates) => {
        const changes = {};
        Object.keys(updates).forEach(key => {
            if (updates[key] !== original[key]) {
                changes[key] = {
                    old: original[key],
                    new: updates[key]
                };
            }
        });
        return changes;
    };

    // Handle revert to version
    const handleRevert = (version) => {
        if (!campaign?.versions) return;

        const targetVersion = campaign.versions.find(v => v.version === version);
        if (!targetVersion?.snapshot) return;

        // Apply snapshot values
        setBudget(targetVersion.snapshot.budget || 0);
        setBudgetType(targetVersion.snapshot.budgetType || 'daily');
        setHeadline(targetVersion.snapshot.headline || '');
        setPrimaryText(targetVersion.snapshot.primaryText || '');
        setCta(targetVersion.snapshot.cta || 'LEARN_MORE');
        setDestinationUrl(targetVersion.snapshot.destinationUrl || '');

        if (targetVersion.snapshot.startDate) {
            setStartDate(new Date(targetVersion.snapshot.startDate).toISOString().split('T')[0]);
        }
        if (targetVersion.snapshot.endDate) {
            setEndDate(new Date(targetVersion.snapshot.endDate).toISOString().split('T')[0]);
        }
    };

    // Navigate to Funnel Builder
    const handleNavigateToFunnel = () => {
        if (campaign?.funnelId && onNavigateToFunnel) {
            onNavigateToFunnel(campaign.funnelId);
            onClose();
        }
    };

    if (!isOpen || !campaign) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: 'var(--space-4)'
            }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '700px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <Edit2 size={20} style={{ color: 'var(--color-primary)' }} />
                        <div>
                            <h3 style={{ margin: 0 }}>Edit Campaign</h3>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        padding: '2px 8px',
                                        borderRadius: 'var(--radius-full)',
                                        background: `${statusConfig.color}20`,
                                        color: statusConfig.color,
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 500
                                    }}
                                >
                                    {React.createElement(statusConfig.icon, { size: 12 })}
                                    {statusConfig.label}
                                </span>
                                <span>•</span>
                                <span>v{campaign.currentVersion || 1}</span>
                                {campaign.platform && (
                                    <>
                                        <span>•</span>
                                        <span style={{ textTransform: 'capitalize' }}>{campaign.platform}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-secondary)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Quick Actions Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-3) var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        {campaign.status === 'ACTIVE' ? (
                            <button
                                onClick={handlePause}
                                disabled={isPausing}
                                className="btn btn-secondary"
                                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                            >
                                <Pause size={16} />
                                {isPausing ? 'Pausing...' : 'Pause'}
                            </button>
                        ) : campaign.status === 'PAUSED' ? (
                            <button
                                onClick={handleResume}
                                disabled={isResuming}
                                className="btn btn-primary"
                                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                            >
                                <Play size={16} />
                                {isResuming ? 'Resuming...' : 'Resume'}
                            </button>
                        ) : null}
                    </div>

                    {campaign.funnelId && (
                        <button
                            onClick={handleNavigateToFunnel}
                            className="btn btn-ghost"
                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                        >
                            <ExternalLink size={16} />
                            Edit in Funnel Builder
                        </button>
                    )}
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-4)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)'
                }}>
                    {/* Messages */}
                    {error && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3)',
                            background: 'var(--error-100)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--error-700)'
                        }}>
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3)',
                            background: 'var(--success-100)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--success-700)'
                        }}>
                            <CheckCircle size={16} />
                            {successMessage}
                        </div>
                    )}

                    {/* Campaign Info Header */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-4)',
                        alignItems: 'flex-start'
                    }}>
                        {/* Creative Preview */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                            {selectedCreativeUrl ? (
                                <img
                                    src={selectedCreativeUrl}
                                    alt={campaign.name}
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 'var(--radius-md)',
                                        objectFit: 'cover',
                                        border: '1px solid var(--color-border)'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 'var(--radius-md)',
                                    background: 'var(--color-bg-tertiary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Image size={32} style={{ color: 'var(--color-text-tertiary)' }} />
                                </div>
                            )}
                            <button
                                onClick={() => setShowCreativeSwapper(!showCreativeSwapper)}
                                style={{
                                    position: 'absolute',
                                    bottom: -8,
                                    right: -8,
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    background: 'var(--color-primary)',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white'
                                }}
                            >
                                <RotateCcw size={14} />
                            </button>
                        </div>

                        {/* Campaign Details */}
                        <div style={{ flex: 1 }}>
                            <h4 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>
                                {campaign.name}
                            </h4>
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                {campaign.funnelStage && <span>{campaign.funnelStage} • </span>}
                                {campaign.objective || 'Conversions'}
                            </div>
                            {campaign.sourcePlanId && (
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)'
                                }}>
                                    From Bulk Planner
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Creative Swapper */}
                    {showCreativeSwapper && libraryItems.length > 0 && (
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                Swap Creative
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                                gap: 'var(--space-2)',
                                maxHeight: 200,
                                overflowY: 'auto'
                            }}>
                                {libraryItems
                                    .filter(item => item.imageUrl)
                                    .slice(0, 20)
                                    .map(item => (
                                        <img
                                            key={item.id}
                                            src={item.imageUrl}
                                            alt={item.title}
                                            onClick={() => {
                                                setSelectedCreativeUrl(item.imageUrl);
                                                setShowCreativeSwapper(false);
                                            }}
                                            style={{
                                                width: '100%',
                                                aspectRatio: '1',
                                                objectFit: 'cover',
                                                borderRadius: 'var(--radius-sm)',
                                                cursor: 'pointer',
                                                border: selectedCreativeUrl === item.imageUrl
                                                    ? '2px solid var(--color-primary)'
                                                    : '1px solid var(--color-border)'
                                            }}
                                        />
                                    ))}
                            </div>
                            <div style={{
                                marginTop: 'var(--space-2)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--warning-600)'
                            }}>
                                Creative changes will sync to the platform when saved
                            </div>
                        </div>
                    )}

                    {/* Tabs */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-1)',
                        borderBottom: '1px solid var(--color-border)',
                        marginBottom: 'var(--space-2)'
                    }}>
                        {[
                            { id: 'budget', label: 'Budget & Schedule', icon: DollarSign },
                            { id: 'copy', label: 'Copy & CTA', icon: Type }
                        ].map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        borderBottom: activeTab === tab.id
                                            ? '2px solid var(--color-primary)'
                                            : '2px solid transparent',
                                        color: activeTab === tab.id
                                            ? 'var(--color-primary)'
                                            : 'var(--color-text-secondary)',
                                        fontWeight: activeTab === tab.id ? 500 : 400,
                                        marginBottom: '-1px'
                                    }}
                                >
                                    <Icon size={16} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Budget & Schedule Tab */}
                    {activeTab === 'budget' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            {/* Budget */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        <DollarSign size={14} />
                                        Budget
                                    </label>
                                    <input
                                        type="number"
                                        value={budget}
                                        onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
                                        min={0}
                                        step={0.01}
                                        className="input-field"
                                        style={{ width: '100%' }}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)',
                                        display: 'block'
                                    }}>
                                        Budget Type
                                    </label>
                                    <select
                                        value={budgetType}
                                        onChange={(e) => setBudgetType(e.target.value)}
                                        className="select-field"
                                        style={{ width: '100%' }}
                                    >
                                        {BUDGET_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Flight Dates */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        <Calendar size={14} />
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="input-field"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        <Calendar size={14} />
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="input-field"
                                        style={{ width: '100%' }}
                                    />
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)',
                                        marginTop: 'var(--space-1)'
                                    }}>
                                        Leave empty for ongoing campaign
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Copy & CTA Tab */}
                    {activeTab === 'copy' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            {/* Headline */}
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    <Type size={14} />
                                    Headline
                                </label>
                                <input
                                    type="text"
                                    value={headline}
                                    onChange={(e) => setHeadline(e.target.value)}
                                    className="input-field"
                                    style={{ width: '100%' }}
                                    placeholder="Ad headline"
                                    maxLength={40}
                                />
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    marginTop: 'var(--space-1)',
                                    textAlign: 'right'
                                }}>
                                    {headline.length}/40
                                </div>
                            </div>

                            {/* Primary Text */}
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    <MessageSquare size={14} />
                                    Primary Text
                                </label>
                                <textarea
                                    value={primaryText}
                                    onChange={(e) => setPrimaryText(e.target.value)}
                                    rows={3}
                                    className="input-field"
                                    style={{ width: '100%', resize: 'vertical' }}
                                    placeholder="Primary text for your ad"
                                    maxLength={125}
                                />
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    marginTop: 'var(--space-1)',
                                    textAlign: 'right'
                                }}>
                                    {primaryText.length}/125
                                </div>
                            </div>

                            {/* CTA */}
                            <div>
                                <label style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-2)',
                                    display: 'block'
                                }}>
                                    Call to Action
                                </label>
                                <select
                                    value={cta}
                                    onChange={(e) => setCta(e.target.value)}
                                    className="select-field"
                                    style={{ width: '100%' }}
                                >
                                    {CTA_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Destination URL */}
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    <Link size={14} />
                                    Destination URL
                                </label>
                                <input
                                    type="url"
                                    value={destinationUrl}
                                    onChange={(e) => setDestinationUrl(e.target.value)}
                                    className="input-field"
                                    style={{ width: '100%' }}
                                    placeholder="https://example.com/landing-page"
                                />
                            </div>
                        </div>
                    )}

                    {/* Version History */}
                    {campaign.versions && campaign.versions.length > 0 && (
                        <div>
                            <button
                                onClick={() => setShowVersionHistory(!showVersionHistory)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-text-secondary)',
                                    padding: 0
                                }}
                            >
                                <History size={14} />
                                Version History ({campaign.versions.length})
                                {showVersionHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>

                            {showVersionHistory && (
                                <div style={{
                                    marginTop: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-md)',
                                    maxHeight: 250,
                                    overflowY: 'auto'
                                }}>
                                    {[...campaign.versions].reverse().map(version => (
                                        <div
                                            key={version.version}
                                            style={{
                                                padding: 'var(--space-2)',
                                                borderBottom: '1px solid var(--color-border)',
                                                fontSize: 'var(--font-size-sm)'
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: 'var(--space-1)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                    <span style={{ fontWeight: 500 }}>
                                                        v{version.version}
                                                    </span>
                                                    {/* Sync status indicator */}
                                                    {version.platformSynced ? (
                                                        <span style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--space-1)',
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--success-500)'
                                                        }}>
                                                            <Check size={12} />
                                                            Synced
                                                        </span>
                                                    ) : version.syncError ? (
                                                        <span style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--space-1)',
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--error-500)'
                                                        }}>
                                                            <XCircle size={12} />
                                                            Sync failed
                                                        </span>
                                                    ) : (
                                                        <span style={{
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--color-text-tertiary)'
                                                        }}>
                                                            Local only
                                                        </span>
                                                    )}
                                                </div>
                                                <span style={{ color: 'var(--color-text-tertiary)' }}>
                                                    {new Date(version.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-secondary)',
                                                marginBottom: 'var(--space-1)'
                                            }}>
                                                {Object.keys(version.changes || {}).map(field => (
                                                    <span key={field} style={{ marginRight: 'var(--space-2)' }}>
                                                        {field}
                                                    </span>
                                                ))}
                                            </div>
                                            <button
                                                onClick={() => handleRevert(version.version)}
                                                style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-primary)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: 0
                                                }}
                                            >
                                                Revert to this version
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-tertiary)',
                    gap: 'var(--space-2)'
                }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges}
                    >
                        {isSaving ? (
                            <>Saving...</>
                        ) : (
                            <>
                                <Save size={16} />
                                Save & Sync
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditCampaignModal;
